<template>
  <div v-if="member"
    class="flex flex-col flex-1 min-h-0 overflow-hidden bg-gradient-to-b from-gray-50 to-white text-left">
    <div class="p-6 flex-1 flex flex-col min-h-0 overflow-hidden">
      <div v-if="!columns || columns.length === 0"
        class="text-center py-16 px-6 bg-white rounded-2xl border-2 border-dashed border-gray-200 shadow-sm">
        <p class="text-gray-500">No columns configured for this board.</p>
      </div>
      <div v-else class="flex gap-4 flex-1 min-h-0 overflow-hidden">
        <tasks-status-section v-for="column in columns" :key="column.id" status="column"
          :tasks="getColumnTasks(column.id)" :total="getColumnTotal(column.id)" :stats-count="getColumnTotal(column.id)"
          :loading="getColumnLoading(column.id)" :has-more="getColumnHasMore(column.id)"
          :title="column.name || 'Unnamed'" section-class="bg-gray-50 border-gray-400" dot-class="bg-gray-500"
          button-class="bg-gray-50 hover:bg-gray-100 border border-gray-200 text-gray-700"
          hover-class="group-hover:text-blue-600 group-hover:bg-blue-100" hover-border-class="hover:border-gray-200"
          :get-priority-color="getPriorityColor" :format-date="formatDate" :get-initials="getInitials"
          :format-time-spent="formatTimeSpent" :format-minutes="formatMinutes"
          :format-estimated-display="formatEstimatedDisplay" :get-progress-bar-color-class="getProgressBarColorClass"
          @task-click="onTaskClick" @load-more="loadMoreMemberTasks(column.id)" />
      </div>
    </div>

    <!-- Task Detail Modal (shown when clicking a task) -->
    <base-modal v-if="selectedTaskId" title="Task Details" @close="selectedTaskId = null">
      <task-detail-modal :task-id="selectedTaskId" :board-members="boardMembers" @close="selectedTaskId = null"
        @updated="onTaskUpdated" @deleted="onTaskDeleted" />
    </base-modal>
  </div>
</template>

<script>
import gql from 'graphql-tag';
import TasksStatusSection from './TasksStatusSection.vue';
import TaskDetailModal from './TaskDetailModal.vue';

export default {
  name: 'MemberTasksModal',
  components: {
    TasksStatusSection,
    TaskDetailModal,
  },
  props: {
    member: {
      type: Object,
      default: null,
    },
    boardId: {
      type: [Number, String],
      required: true,
    },
    columns: {
      type: Array,
      default: () => [],
    },
    memberStats: {
      type: Object,
      default: () => ({ total: 0, todo: 0, in_progress: 0, paused: 0, in_review: 0, done: 0 }),
    },
    boardCreatorId: {
      type: [Number, String],
      default: null,
    },
    boardMembers: {
      type: Array,
      default: () => [],
    },
  },
  data() {
    return {
      memberTasksData: {},
      memberTasksLoading: {},
      selectedTaskId: null,
    };
  },
  mounted() {
    this.$nextTick(() => {
      this.maybeFetchMemberTasks();
    });
  },
  watch: {
    member: {
      handler(newVal) {
        if (newVal) {
          this.selectedTaskId = null;
          this.resetMemberTasksData();
          this.maybeFetchMemberTasks();
        }
      },
      immediate: true,
    },
    columns: {
      handler(cols) {
        if (cols && cols.length > 0) {
          this.resetMemberTasksData();
          this.maybeFetchMemberTasks();
        }
      },
      immediate: true,
    },
  },
  methods: {
    getPriorityColor(priority) {
      const colors = { low: '#10B981', medium: '#499DD4', high: '#F59E0B', critical: '#EF4444' };
      return colors[priority] || '#9BA4B5';
    },
    formatDate(date) {
      if (!date) return '';
      return new Date(date).toLocaleDateString('en-US', { month: 'short', day: 'numeric' });
    },
    getInitials(name) {
      if (!name) return '?';
      return name.split(' ').map(n => n[0]).join('').toUpperCase().slice(0, 2);
    },
    formatEstimatedDisplay(minutes, type) {
      if (!minutes || minutes < 0) return '0m';
      if (type === 'days') return (minutes / 1440 === 1 ? '1 day' : Math.round(minutes / 1440) + ' days');
      return this.formatMinutes(minutes);
    },
    formatMinutes(minutes) {
      if (!minutes || minutes < 0) return '0m';
      if (minutes < 60) return minutes + 'm';
      const h = Math.floor(minutes / 60);
      const m = minutes % 60;
      return m > 0 ? h + 'h ' + m + 'm' : h + 'h';
    },
    formatTimeSpent(seconds) {
      if (seconds == null || seconds < 0) return '0s';
      const s = Math.floor(seconds);
      if (s < 60) return s + 's';
      const m = Math.floor(s / 60);
      const secs = s % 60;
      if (m < 60) return secs > 0 ? m + 'm ' + secs + 's' : m + 'm';
      const h = Math.floor(m / 60);
      const mins = m % 60;
      return mins > 0 || secs > 0 ? h + 'h ' + mins + 'm ' + secs + 's' : h + 'h';
    },
    getProgressBarColorClass(percent) {
      if (percent == null) return 'bg-blue-500';
      if (percent > 100) return 'bg-red-500';
      if (percent >= 90) return 'bg-amber-500';
      return 'bg-blue-500';
    },
    onTaskClick(taskId) {
      this.selectedTaskId = Number(taskId) || taskId;
    },
    onTaskUpdated() {
      this.fetchMemberTasksForAllColumns();
      this.$emit('task-updated');
    },
    onTaskDeleted() {
      this.selectedTaskId = null;
      this.fetchMemberTasksForAllColumns();
      this.$emit('task-deleted');
    },
    resetMemberTasksData() {
      const defaults = { tasks: [], page: 1, total: 0, hasMore: false };
      this.memberTasksData = {};
      this.memberTasksLoading = {};
      if (this.columns && this.columns.length > 0) {
        this.columns.forEach((col) => {
          const cid = String(col.id);
          this.memberTasksData[cid] = { ...defaults };
          this.memberTasksLoading[cid] = false;
        });
      }
    },
    getColumnTasks(columnId) {
      const cid = String(columnId);
      const data = this.memberTasksData[cid];
      return data && data.tasks ? data.tasks : [];
    },
    getColumnTotal(columnId) {
      const cid = String(columnId);
      const data = this.memberTasksData[cid];
      return data ? (data.total || 0) : 0;
    },
    getColumnHasMore(columnId) {
      const cid = String(columnId);
      const data = this.memberTasksData[cid];
      return data ? (data.hasMore || false) : false;
    },
    getColumnLoading(columnId) {
      const cid = String(columnId);
      return this.memberTasksLoading[cid] || false;
    },
    maybeFetchMemberTasks() {
      if (!this.member || !this.boardId || !this.columns || this.columns.length === 0) return;
      this.fetchMemberTasksForAllColumns();
    },
    async fetchMemberTasksForAllColumns() {
      if (!this.member || !this.boardId || !this.columns || this.columns.length === 0) return;
      const promises = this.columns.map((col) => this.fetchMemberTasksByColumn(col.id, 1, true));
      await Promise.all(promises);
    },
    async fetchMemberTasksByColumn(columnId, page, replace) {
      if (!this.member || !this.boardId) return;
      const cid = String(columnId);
      if (!this.memberTasksData[cid]) {
        this.memberTasksData = Object.assign({}, this.memberTasksData, { [cid]: { tasks: [], page: 1, total: 0, hasMore: false } });
      }
      this.memberTasksLoading = Object.assign({}, this.memberTasksLoading, { [cid]: true });
      try {
        const result = await this.$apollo.query({
          query: gql`
            query GetMemberTasksByColumn($board_id: ID!, $column_id: ID!, $assignee_id: ID!, $first: Int, $page: Int) {
              memberTasksByColumn(board_id: $board_id, column_id: $column_id, assignee_id: $assignee_id, first: $first, page: $page) {
                data {
                  id token title description priority status due_date completed_at
                  estimated_minutes estimated_time_type actual_seconds_spent time_progress_percent checklist_progress_percent is_timer_running
                  column { id name }
                  assignees { id name email }
                  viewers { id name email }
                  created_by { id name }
                }
                paginatorInfo { total currentPage hasMorePages perPage }
              }
            }
          `,
          variables: {
            board_id: this.boardId,
            column_id: columnId,
            assignee_id: this.member.id,
            first: 10,
            page: page || 1,
          },
          fetchPolicy: 'network-only',
        });
        const payload = result.data && result.data.memberTasksByColumn;
        if (!payload) return;
        const tasks = payload.data || [];
        const paginator = payload.paginatorInfo || {};
        if (replace) {
          this.memberTasksData[cid].tasks = tasks;
        } else {
          this.memberTasksData[cid].tasks = this.memberTasksData[cid].tasks.concat(tasks);
        }
        this.memberTasksData[cid].page = paginator.currentPage || page;
        this.memberTasksData[cid].total = paginator.total || 0;
        this.memberTasksData[cid].hasMore = paginator.hasMorePages || false;
      } catch (e) {
        console.error('Error fetching member tasks by column:', e);
      } finally {
        this.memberTasksLoading = Object.assign({}, this.memberTasksLoading, { [cid]: false });
      }
    },
    async loadMoreMemberTasks(columnId) {
      const cid = String(columnId);
      const data = this.memberTasksData[cid];
      const nextPage = (data && data.page ? data.page : 1) + 1;
      await this.fetchMemberTasksByColumn(columnId, nextPage, false);
    },
  },
};
</script>
